/**
 * What a dig is being asked to do, and where.
 *
 * TWO INDEPENDENT QUESTIONS, and the room kept answering one of them for you.
 * "Dig wider" meant the whole room; "Dig deeper" meant the whole room too, and
 * later meant the table you were reading — which paired them arbitrarily. They
 * do not pair:
 *
 *            │ this table              │ the whole room
 *   ─────────┼─────────────────────────┼──────────────────────────
 *   wider    │ find more complaints     │ new players, pains, searches
 *   deeper   │ fill its empty cells     │ work every table's gaps
 *
 * All four are real asks, so both get asked — once, in the window that already
 * opens before anything spends.
 *
 * Pure, no React, so CRA's jest can test it (the rule that came out of the
 * router-v7 problem). Everything here is a function of WHERE THE READER IS,
 * which is what makes the default right without anyone choosing it.
 */

export type Depth = 'WIDER' | 'DEEPER';

/** One thing a dig can be aimed at. `key` is what the radio group tracks. */
export interface AimScope {
  key: string;
  kind: 'room' | 'table' | 'item' | 'lens' | 'brand';
  /** What it is called, in the room's own words. */
  label: string;
  /** The number under it: what is short, or what is inside. Never invented —
   *  an option with nothing to say says nothing. */
  what: string;
  /** Set for a table scope: the report key the run is filling. */
  report?: string;
  /** Set for an item scope: the row being read. For a brand scope: the company. */
  itemId?: number;
}

export interface Aim {
  depth: Depth;
  /** An `AimScope.key`. */
  scope: string;
}

/** Where the reader is, and what the board knows about it. */
export interface Place {
  stage: string;
  /** The table on screen, when one is. */
  table?: { key: string; label: string; holes: number } | null;
  /** The row on screen, when one is. */
  item?: { id: number; title: string } | null;
  /** One company's brand searches, when that report is on screen. */
  brand?: { id: number; title: string; searches: number } | null;
  /** The lens, already worded — "style: skate · confident". Empty when off. */
  lensLabel?: string;
  /** Every table the room has, for the room option's count. */
  tables?: { holes: number }[];
  /** The lead the room follows. Every dig is inside it, so "the whole room"
   *  is the whole lead. */
  lead?: string | null;
}

export const ROOM: AimScope = {
  key: 'room',
  kind: 'room',
  label: 'The whole room',
  what: '',
};

/**
 * The places this dig could be aimed, MOST SPECIFIC FIRST.
 *
 * Order is the answer to "what did you mean": a person reading one company's
 * page who presses Research means that company, and a list that opens with the
 * whole room makes them work to say so.
 */
export function scopesFor(place: Place): AimScope[] {
  const out: AimScope[] = [];
  // ONE COMPANY'S SEARCHES: find more of them, or search the same ones again.
  if (place.brand) {
    const n = place.brand.searches;
    out.push({
      key: `brand:${place.brand.id}`,
      kind: 'brand',
      label: `Brand searches · ${place.brand.title}`,
      what: n ? `${n} search${n === 1 ? '' : 'es'} so far` : 'not searched yet',
      itemId: place.brand.id,
    });
  }
  if (place.item) {
    out.push({
      key: `item:${place.item.id}`,
      kind: 'item',
      label: place.item.title,
      what: 'just this one',
      itemId: place.item.id,
    });
  }
  if (place.table) {
    const n = place.table.holes;
    out.push({
      key: `table:${place.table.key}`,
      kind: 'table',
      label: place.table.label,
      // A table with nothing missing is still worth widening — "find more" is
      // a fine thing to ask of a full table — so it is offered either way, and
      // only the number changes.
      what: n
        ? `${n} gap${n === 1 ? '' : 's'} in it`
        : 'nothing missing on it',
      report: place.table.key,
    });
  }
  // Following a lead, the lens holds the lead's own chips: it is somewhere
  // narrower only when chips were added past them (the page says so by
  // passing a label at all).
  if ((place.lensLabel || '').trim()) {
    out.push({
      key: 'lens',
      kind: 'lens',
      label: 'Inside the lens',
      what: (place.lensLabel || '').trim(),
    });
  }
  const holes = (place.tables ?? []).reduce((n, t) => n + (t.holes || 0), 0);
  const tables = (place.tables ?? []).filter(t => t.holes > 0).length;
  out.push({
    ...ROOM,
    // EVERY DIG IS INSIDE THE LEAD, so this choice is all of the lead.
    label: place.lead ? `All of ${place.lead}` : ROOM.label,
    what: holes
      ? `${holes} thing${holes === 1 ? '' : 's'} short across ${tables} table${tables === 1 ? '' : 's'}`
      : '',
  });
  return out;
}

/**
 * What to have selected when the window opens.
 *
 * Find more, across the whole room, until the person picks something else —
 * then whatever they picked last. A new room's tables are short of nearly
 * everything, so defaulting to "fill the gaps" sent the first dig filling
 * holes in an almost empty board. Once they have chosen to fill gaps, that is
 * their call and it sticks.
 *
 * A remembered place that is not on screen now (a table they have left) keeps
 * their choice of find more / fill the gaps, aimed at the whole room.
 *
 * THE ROOM'S RECOMMENDATION WINS over the last choice when the dig is aimed
 * at the whole room (`boardroom/next_dig.py`): the last choice stuck, so room
 * 54 ran ten Find more digs and never filled a gap. It is advice about the
 * room, so a table or an item keeps the person's choice. They can still pick
 * the other.
 */
export function defaultAim(scopes: AimScope[], last?: Aim | null, recommended?: Depth | null): Aim {
  // ONE THING OPEN IS WHAT THE BUTTON IS ABOUT. With a pain or a company open,
  // pressing Research and getting "the whole room" was the wrong question
  // answered: it is aimed at the item, finding more, unless they last chose
  // this same item.
  const one = scopes.find(s => s.kind === 'item' || s.kind === 'brand');
  if (one) return last?.scope === one.key ? last : { depth: 'WIDER', scope: one.key };
  if (!last) return { depth: recommended ?? 'WIDER', scope: ROOM.key };
  const scope = scopes.some(s => s.key === last.scope) ? last.scope : ROOM.key;
  return { depth: (scope === ROOM.key && recommended) || last.depth, scope };
}

export function scopeByKey(scopes: AimScope[], key: string): AimScope {
  return scopes.find(s => s.key === key) ?? scopes[scopes.length - 1] ?? ROOM;
}

/** The one line that says what is about to happen, for the window's heading
 *  and the button's title. Reads as a sentence, not a pair of settings. */
export function aimLabel(depth: Depth, scope: AimScope): string {
  const where =
    scope.kind === 'room'
      ? // "the whole room", or under a lead "all of <lead>".
        scope.label === ROOM.label
        ? 'the whole room'
        : scope.label.charAt(0).toLowerCase() + scope.label.slice(1)
      : scope.kind === 'lens'
        ? `everything inside ${scope.what}`
        : scope.label.toLowerCase();
  return depth === 'WIDER' ? `Find more for ${where}` : `Fill the gaps in ${where}`;
}

/** What each choice means, said in terms of what it will DO rather than what
 *  it is called. These are the words next to the radio. */
export const DEPTH_WORDS: Record<Depth, { label: string; hint: string }> = {
  WIDER: {
    label: 'Find more',
    hint: 'Look for things the room has not got yet - new players, new complaints, new searches.',
  },
  DEEPER: {
    label: 'Fill the gaps',
    hint: 'Work what is already here - read the pages, get the prices, find a second source.',
  },
};

/** The words next to a choice, for where it is aimed. On one company or
 *  product, "find more" is a deep dive, not "new players" — that wording sent
 *  the reader looking for other companies when the run would stay on this one. */
export function depthHint(depth: Depth, scope?: AimScope | null): string {
  if (scope?.kind === 'brand') {
    return depth === 'WIDER'
      ? 'Add more searches about it: the related searches Google suggested that carry its name, all its products, and more rivals. No dig - just searches.'
      : 'Search the same set again: retry the ones that failed and refresh any over a week old. No dig - just searches.';
  }
  if (scope?.kind === 'item') {
    return depth === 'WIDER'
      ? 'Deep dive: every seat digs into this one from its own angle - what people say, facts and figures, links, demand.'
      : 'Finish what this one is missing - its site, pages, products, prices.';
  }
  return DEPTH_WORDS[depth].hint;
}

/** What a RUNNING dig was asked to do, and where, in the Research window's own
 *  words: "Find more · The whole room", "Fill the gaps · Competitors". Shown in
 *  the status bar beside Stop, so the dig on screen says what it is for.
 *
 *  `tableLabel` names a report key ("competitors" → "Competitors"); `itemLabel`
 *  names a board item when the page knows it. Pure. */
export function runLabel(
  run: {
    intent: string;
    focus_item_id?: number | null;
    scope?: { tables?: string[]; tags?: string[]; verified?: string | null; by?: string | null } | null;
    lead_id?: number | null;
  },
  tableLabel: (key: string) => string | undefined,
  itemLabel: (id: number) => string | undefined = () => undefined,
  leadLabel: (id: number) => string | undefined = () => undefined,
): string {
  if (run.intent === 'WRAP_UP') return 'Reading what landed';
  const what = DEPTH_WORDS[run.intent as Depth]?.label ?? 'Digging';
  const scope = run.scope ?? null;
  const tables = scope?.tables ?? [];
  let where = ROOM.label;
  if (run.focus_item_id) {
    where = itemLabel(run.focus_item_id) ?? `#${run.focus_item_id}`;
  } else if (tables.length) {
    where = tables.map(t => tableLabel(t) ?? t.replace(/_/g, ' ')).join(', ');
  }
  // INSIDE A LEAD: its name, so the bar does not say "The whole room" for a
  // dig that cannot leave the lead. A table dig says both.
  const lead = run.lead_id ? leadLabel(run.lead_id) ?? 'a lead' : null;
  const named = Boolean(run.focus_item_id || tables.length);
  if (lead) {
    where = named ? `${where} · inside ${lead}` : `Inside ${lead}`;
  } else if (!named && scope && ((scope.tags?.length ?? 0) > 0 || scope.verified || scope.by)) {
    where = 'A narrowed view';
  }
  return `${what} · ${where}`;
}
